import {
    ClaimCloseoutType,
    ClaimType,
    getOptions,
    ISelectOption,
    ProviderClaim,
    ProviderUpdateClaimPayload,
    CloseoutForm,
    CloseoutFormValues,
    normalizeSelectValue,
    resolveCloseoutVoid,
} from '@enter/core';
import React, { useMemo } from 'react';
import { ClaimStore } from 'stores';

export enum ProviderClaimCloseoutDescription {
    CLAIM_CYCLE_COMPLETED = 'CLAIM_CYCLE_COMPLETED',
    DUPLICATE_CLAIM = 'DUPLICATE_CLAIM',
    PROVIDER_DECLINED_TO_BILL_GUARANTOR = 'PROVIDER_DECLINED_TO_BILL_GUARANTOR',
    CHARITY_CARE = 'CHARITY_CARE',
    PROVIDER_SIGNED_NEGOTIATION = 'PROVIDER_SIGNED_NEGOTIATION',
    PROVIDER_CODING_INVALID_AND_UNCHANGED = 'PROVIDER_CODING_INVALID_AND_UNCHANGED',
    PROVIDER_NONRESPONSIVE = 'PROVIDER_NONRESPONSIVE',
    PROVIDER_CLAIM_SERVICE_LINES_INVALID = 'PROVIDER_CLAIM_SERVICE_LINES_INVALID',
    INVALID_PRIOR_AUTHORIZATION = 'INVALID_PRIOR_AUTHORIZATION',
    INVALID_INFORMATION_PAYER_ADJUDICATION = 'INVALID_INFORMATION_PAYER_ADJUDICATION',
    INVALID_INFORMATION_PATIENT = 'INVALID_INFORMATION_PATIENT',
    INVALID_INFORMATION_PROVIDER = 'INVALID_INFORMATION_PROVIDER',
    INVALID_INFORMATION_GUARANTOR = 'INVALID_INFORMATION_GUARANTOR',
    INVALID_INFORMATION_FACILITY = 'INVALID_INFORMATION_FACILITY',
    INVALID_INFORMATION_SERVICES = 'INVALID_INFORMATION_SERVICES',
    INVALID_INFORMATION_DIAGNOSIS = 'INVALID_INFORMATION_DIAGNOSIS',
    INVALID_INFORMATION_DOCUMENTATION = 'INVALID_INFORMATION_DOCUMENTATION',
    INVALID_INFORMATION_PRIOR_AUTHORIZATION = 'INVALID_INFORMATION_PRIOR_AUTHORIZATION',
    INVALID_HL7_MESSAGE = 'INVALID_HL7_MESSAGE',
    MISSING_INFORMATION_DOCUMENTATION = 'MISSING_INFORMATION_DOCUMENTATION',
    MISSING_INFORMATION_PRIOR_AUTHORIZATION = 'MISSING_INFORMATION_PRIOR_AUTHORIZATION',
    MISSING_INFORMATION_SECONDARY_PAYER = 'MISSING_INFORMATION_SECONDARY_PAYER',
    TIMELY_FILING = 'TIMELY_FILING',
}

interface ICloseoutProps {
    claim: ProviderClaim;
    onSubmit: (claim: ProviderClaim) => void;
    onCancel: () => void;
}

export const ProviderCloseoutDialog: React.FC<ICloseoutProps> = ({ claim, onSubmit, onCancel }) => {
    const descriptionOptions: ISelectOption[] = useMemo(
        () => getOptions(ProviderClaimCloseoutDescription, { normalize: true }),
        [],
    );

    const voidLocked = !!claim.closeoutVoid;

    const initialValues: CloseoutFormValues = {
        closeoutType: voidLocked
            ? ClaimCloseoutType.CLOSED
            : (claim.closeoutType as ClaimCloseoutType) ?? ClaimCloseoutType.OPEN,
        closeoutDescription: normalizeSelectValue(
            claim.closeoutDescription as string | undefined,
            descriptionOptions,
        ),
        closeoutVoid: voidLocked,
    };

    const handleSubmit = async (values: CloseoutFormValues): Promise<void> => {
        const closeoutVoid = resolveCloseoutVoid(values.closeoutType, values.closeoutVoid, voidLocked);
        const closeoutType = closeoutVoid ? ClaimCloseoutType.CLOSED : values.closeoutType;

        const updatedClaim: ProviderClaim = {
            ...claim,
            type: claim.type || ClaimType.PROFESSIONAL,
            closeoutType,
            closeoutDescription: values.closeoutDescription,
            closeoutVoid,
        };

        const payload: ProviderUpdateClaimPayload = {
            closeoutType,
            closeoutDescription: updatedClaim.closeoutDescription,
            closeoutVoid,
        };

        await ClaimStore.update(claim.id as string, payload);
        onSubmit(updatedClaim);
    };

    return (
        <CloseoutForm
            initialValues={initialValues}
            descriptionOptions={descriptionOptions}
            onSubmit={handleSubmit}
            onCancel={onCancel}
            voidLocked={voidLocked}
        />
    );
};